import React from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline'
import type { PasswordRequirement } from '@/utils/passwordValidation'
import { usePasswordPolicy } from '@/hooks/usePasswordPolicy'

interface PasswordRequirementsListProps {
  password: string
  className?: string
  showOnlyUnmet?: boolean
  /**
   * #623 — optional override so a parent that already resolved the policy
   * (e.g. via usePasswordPolicy) can pass its requirement list directly. When
   * omitted, this component fetches the live admin policy itself (React Query
   * dedupes the shared request).
   */
  requirements?: PasswordRequirement[]
}

export const PasswordRequirementsList: React.FC<PasswordRequirementsListProps> = ({
  password,
  className = '',
  showOnlyUnmet = false,
  requirements: requirementsProp,
}) => {
  const { t } = useTranslation('auth')
  const { requirements: policyRequirements } = usePasswordPolicy()
  const source = requirementsProp ?? policyRequirements
  const requirements = source.map(req => ({
    ...req,
    label: t(`completeAccount.req.${req.id}`, { defaultValue: req.label, ...req.labelParams }),
    met: req.test(password),
  }))

  const displayRequirements = showOnlyUnmet
    ? requirements.filter(req => !req.met)
    : requirements

  // Only show if password has been entered or we want to show all requirements
  if (!password && showOnlyUnmet) {
    return null
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <p className="text-sm font-medium text-neutral-700">Password requirements:</p>
      <ul className="space-y-1">
        {displayRequirements.map((requirement) => (
          <li
            key={requirement.id}
            className="flex items-start space-x-2 text-sm"
          >
            {requirement.met ? (
              <CheckCircleIcon className="h-4 w-4 text-success-600 flex-shrink-0 mt-0.5" />
            ) : (
              <XCircleIcon className="h-4 w-4 text-error-500 flex-shrink-0 mt-0.5" />
            )}
            <span
              className={`${
                requirement.met ? 'text-success-700' : 'text-neutral-600'
              }`}
            >
              {requirement.label}
            </span>
          </li>
        ))}
      </ul>

      {/* Show all requirements met indicator */}
      {password && requirements.every(req => req.met) && (
        <div className="mt-3 p-2 bg-success-50 border border-success-200 rounded-md">
          <p className="text-sm text-success-800 font-medium">
            ✓ All requirements met
          </p>
        </div>
      )}
    </div>
  )
}
